import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useGetRolesQuery, useGetUsersQuery } from 'api/main';
import { userSelectors } from 'reducers';
import { userSchemas } from 'schemas';
import { useDebounce, useLocalStorageFilters, useUserRoleMatchCategory } from 'utils/hooks';
import {
    listingActionButtonStyle,
    ORDER_BY,
    roleCategories,
    sortDirection,
    UserEnrichments,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import DistributorUserRow from './DistributorUserRow';
import DistributorUserFilters from './DistributorUserFilters';
import {
    ActionButton,
    CustomTablePagination,
    EmptyTableBody,
    EnhancedTableHead,
    QuickSearchField,
    FiltersButton,
} from 'components';
import {
    Typography,
    TableBody,
    Table,
    TableContainer,
    TableFooter,
    TableRow,
    Box,
} from '@mui/material';

const getTableHeads = userIsServicer => {
    const heads = [
        { id: 'online', isSortable: false, label: texts.ONLINE },
        { id: ORDER_BY.lastName, isSortable: true, label: texts.NOM },
    ];

    if (userIsServicer) {
        heads.push({
            id: 'distributorCompanyName',
            isSortable: false,
            label: texts.DISTRIBUTOR_COMPANY_NAME,
        });
    }

    heads.push(
        { id: ORDER_BY.email, isSortable: true, label: texts.ADRESSE_EMAIL },
        { id: 'phoneNumber', isSortable: false, label: texts.PHONE_NUMBER },
        { id: ORDER_BY.role, isSortable: true, label: texts.ROLE },
        { id: 'tag', isSortable: false, label: texts.COMPLEMENT },
        { id: 'status', isSortable: false, label: texts.STATUT },
        { id: ORDER_BY.login, isSortable: true, label: texts.LAST_LOGIN_DATE },
        { id: 'actions', isSortable: false, label: '' }
    );

    return heads;
};

const DistributorUsersTable = () => {
    const navigate = useNavigate();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);

    const tableHeads = useMemo(() => getTableHeads(userIsServicer), [userIsServicer]);

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();

    const activeImpersonation = useMemo(() => !!internalUser?.impersonatedByUser, [internalUser]);
    const adminDistributorRoles = useMemo(
        () =>
            roles.filter(
                r =>
                    r.category?.name === roleCategories.ADMIN_DISTRIBUTORS ||
                    r.category?.name === roleCategories.DISTRIBUTORS
            ),
        [roles]
    );
    const defaultFilters = useMemo(
        () => ({
            page: 1,
            orderBy: ORDER_BY.id,
            direction: sortDirection.DESC,
            companyId: [currentSubscription?.distributorCompany?.id],
            keyword: '',
            role: [],
            enrichWith: [
                UserEnrichments.ROLES,
                UserEnrichments.DISTRIBUTOR_COMPANY,
                UserEnrichments.SUBSCRIPTIONS,
            ],
        }),
        [currentSubscription]
    );

    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useLocalStorageFilters(
        'distributorUsersFilters',
        defaultFilters,
        activeImpersonation
    );
    const {
        data: { userList = [], lastPage } = { userList: [], lastPage: 1 },
        isFetching: listLoader,
    } = useGetUsersQuery(
        userSchemas.getUsers({
            ...filters,
            role: adminDistributorRoles.map(role => role.id),
        }),
        { skip: !roles.length }
    );

    const handlePageChange = useCallback(
        page => () => setFilters(oldValue => ({ ...oldValue, page })),
        [setFilters]
    );

    const handleSort = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    const handleApplyFilters = useCallback(
        customFilters => {
            setFilters(prevState => ({ ...prevState, ...customFilters, page: 1 }));
        },
        [setFilters]
    );

    const handleToggleFilters = useCallback(() => setFiltersOpen(prevState => !prevState), []);

    useEffect(() => {
        if (filters.page > lastPage && lastPage > 0) {
            setFilters(prevState => ({ ...prevState, page: lastPage }));
        }
    }, [lastPage, filters, setFilters]);

    useEffect(() => {
        setFilters(prevState => ({
            ...prevState,
            page: 1,
            keyword: debouncedKeyword,
            orderBy: !!debouncedKeyword ? prevState.orderBy : ORDER_BY.id,
            direction: !!debouncedKeyword ? prevState.direction : sortDirection.DESC,
        }));
    }, [debouncedKeyword, setFilters]);

    return (
        <React.Fragment>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h5" align="left" gutterBottom>
                    {userIsServicer ? texts.DISTRIBUTOR_USERS : texts.USERS}
                </Typography>
                {!userIsServicer && (
                    <Box display="flex" alignItems="flex-end" flexDirection="column">
                        <ActionButton
                            variant="outlined"
                            sx={listingActionButtonStyle}
                            onClick={() => navigate('invite')}
                        >
                            {texts.CREATE_USER}
                        </ActionButton>
                    </Box>
                )}
            </Box>
            <Box display="flex" marginY={3} alignItems="center">
                <QuickSearchField
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    handleClear={() => setKeyword('')}
                />
                {userIsServicer && <FiltersButton onClick={handleToggleFilters} />}
            </Box>
            {!listLoader && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            {userIsServicer && (
                <DistributorUserFilters
                    open={filtersOpen}
                    applyFilters={handleApplyFilters}
                    defaultFilters={defaultFilters}
                    savedFilters={filters}
                    loading={listLoader}
                />
            )}
            <TableContainer>
                <Table>
                    <EnhancedTableHead
                        headCells={tableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSort}
                    />
                    {listLoader || rolesLoader || userList.length === 0 ? (
                        <EmptyTableBody
                            colSpan={tableHeads.length}
                            loading={listLoader || rolesLoader}
                        />
                    ) : (
                        <TableBody>
                            {userList?.map(user => (
                                <DistributorUserRow
                                    key={user.id}
                                    user={user}
                                    internalUser={internalUser}
                                    userIsServicer={userIsServicer}
                                />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={tableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default DistributorUsersTable;
